import type { AuthFetch } from "@core/auth/authFetch";
import { createHttpError } from "@core/http/HttpError";
import { buildQueryString } from "@core/http/queryString";

export interface ApiClientOptions<Service extends string> {
  // Path prefix each service is mounted under, e.g. { psa: "/api/psa" }
  servicePaths: Record<Service, string>;
  // A getter, not a value: the Pinia auth store it comes from only exists once the app is running
  authFetch: () => AuthFetch;
  // Also a getter: the portal only gets its API origin from /config.json during bootstrap. Defaults to the app's own origin
  baseUrl?: () => string;
  // Returns test data for a path, or null to let the request through (Mobius test mode)
  resolveMock?: (service: Service, path: string) => Promise<unknown | null>;
}

export interface ApiClient<Service extends string> {
  fetchPage<T = unknown>(service: Service, path: string, params?: Record<string, unknown>): Promise<T>;
  postPage<T = unknown, B extends object | FormData = object>(
    service: Service,
    path: string,
    data?: B,
    params?: Record<string, unknown>,
    method?: string
  ): Promise<T>;
  deletePage<T = unknown>(service: Service, path: string, params?: Record<string, unknown>): Promise<T>;
}

// A 204 has no body, so the caller gets the Response itself rather than a parse error
async function parseBody<T>(response: Response): Promise<T> {
  if (response.status === 204) return response as T;

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("text/plain")) return response.text() as Promise<T>;

  return response.json() as Promise<T>;
}

// Builds the fetchPage / postPage / deletePage helpers that an app's services call.
// These are essentially what axios does: build the URL, add the auth header, turn failures into an HttpError, parse the body.
// Whatever differs between Mobius and the Customer Portal (service paths, API origin, test data) is passed in.
// The helpers are generic over the response type. Each call site declares the shape it expects, and nothing is checked at runtime.
export function createApiClient<Service extends string>(options: ApiClientOptions<Service>): ApiClient<Service> {
  const { servicePaths, authFetch, baseUrl, resolveMock } = options;

  function constructUrl(service: Service, path: string, params: Record<string, unknown>): string {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${baseUrl?.() ?? ""}${servicePaths[service]}${cleanPath}${buildQueryString(params)}`;
  }

  async function request<T>(
    service: Service,
    path: string,
    params: Record<string, unknown>,
    init: RequestInit
  ): Promise<T> {
    const fullUrl = constructUrl(service, path, params);
    const response = await authFetch()(fullUrl, init);
    if (!response.ok) throw await createHttpError(response, fullUrl);
    return parseBody<T>(response);
  }

  return {
    async fetchPage<T = unknown>(service: Service, path: string, params: Record<string, unknown> = {}): Promise<T> {
      const mockData = (await resolveMock?.(service, path)) ?? null;
      if (mockData !== null) return mockData as T;

      return request<T>(service, path, params, { method: "GET" });
    },

    postPage<T = unknown, B extends object | FormData = object>(
      service: Service,
      path: string,
      data?: B,
      params: Record<string, unknown> = {},
      method = "POST"
    ): Promise<T> {
      const isFormData = data instanceof FormData;
      const headers: Record<string, string> = {};
      if (!isFormData) headers["Content-Type"] = "application/json";

      return request<T>(service, path, params, {
        method,
        headers,
        body: isFormData ? data : JSON.stringify(data ?? {})
      });
    },

    deletePage<T = unknown>(service: Service, path: string, params: Record<string, unknown> = {}): Promise<T> {
      return request<T>(service, path, params, { method: "DELETE" });
    }
  };
}
